import React, { useState, useEffect } from 'react';
import DynamicComponent from './DynamicComponent';
import { standardizeComponentProps } from '../../../utils/ComponentPropTransformer';

/**
 * BaseEmailComponent - A flexible component that can render any email section
 * based on standardized props. This component is used as a fallback when a specific
 * component type is not found.
 */
const BaseEmailComponent = (props) => {
  // Ensure props are standardized
  const [componentData, setComponentData] = useState(() => standardizeComponentProps(props));

  // Re-standardize props when they change
  useEffect(() => {
    setComponentData(standardizeComponentProps(props));
  }, [props]);

  // Extract common data
  const { 
    id, 
    component_name, 
    title, 
    description, 
    image, 
    features = [],
    buttonText,
    buttonUrl,
    previewMode,
    onUpdate
  } = componentData;

  // Handle updates to this component
  const handleUpdate = (newData) => {
    if (onUpdate && typeof onUpdate === 'function') {
      // Always standardize the data before passing it up
      const standardizedData = standardizeComponentProps({
        ...componentData,
        ...newData
      });
      
      setComponentData(standardizedData);
      onUpdate(standardizedData, id);
    } else {
      console.warn('BaseEmailComponent: onUpdate function not provided', { id, component_name });
      // Still update local state even if we can't propagate changes
      setComponentData(standardizeComponentProps({
        ...componentData,
        ...newData
      }));
    }
  };

  // Render any sub-components/features if they exist
  const renderFeatures = () => {
    if (!features || !Array.isArray(features) || features.length === 0) {
      return null;
    }

    return (
      <div className="base-component-features">
        {features.map((feature, index) => {
          // Standardize each feature's props
          const standardizedFeature = standardizeComponentProps(feature);
          return (
            <DynamicComponent 
              key={`feature-${index}`}
              {...standardizedFeature}
              // Pass down update function that updates this specific feature
              onUpdate={(updatedFeature) => {
                const updatedFeatures = [...features];
                updatedFeatures[index] = {
                  ...standardizedFeature,
                  ...updatedFeature
                };
                handleUpdate({ features: updatedFeatures });
              }}
              previewMode={previewMode}
            />
          );
        })}
      </div>
    );
  };

  // Render a button if button data is present
  const renderButton = () => {
    if (!buttonText && !buttonUrl) return null;

    return (
      <div className="base-component-button mt-3">
        <a 
          href={buttonUrl || '#'} 
          className="btn btn-primary"
          style={{ 
            backgroundColor: componentData.buttonColor || '#007bff',
            color: componentData.buttonTextColor || '#ffffff'
          }}
        >
          {buttonText || 'Click Here'}
        </a>
      </div>
    );
  };

  // If this is purely a container for other components, just render the features
  if (!title && !description && !image && features.length > 0) {
    return renderFeatures();
  }

  // Otherwise render a basic component structure
  return (
    <div className={`base-email-component ${previewMode ? 'preview-mode' : ''}`} id={id}>
      {/* Component Header - Only show in edit mode */}
      {!previewMode && title && (
        <h2 className="base-component-title">
          {title}
        </h2>
      )}
      
      {/* Preview mode titles */}
      {previewMode && title && (
        <h2 className="preview-title">
          {title}
        </h2>
      )}
      
      {/* Main Content */}
      <div className="base-component-content">
        {/* Show image if provided */}
        {image && (
          <div className="base-component-image mb-3">
            <img 
              src={image} 
              alt={title || 'Component image'} 
              className="img-fluid" 
              style={{ maxWidth: '100%' }}
            />
          </div>
        )}
        
        {/* Description */}
        {description && (
          <div className="base-component-description">
            {description}
          </div>
        )}
        
        {/* Button if present */}
        {renderButton()}
        
        {/* Nested components/features */}
        {renderFeatures()}
      </div>
    </div>
  );
};

export default BaseEmailComponent; 